import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import RefreshButton from "@/components/RefreshButton";
import StatsPanel from "@/components/StatsPanel";
import KanbanBoard from "@/components/KanbanBoard";
import MembersList from "@/components/MembersList";
import MemberFormDialog from "@/components/MemberFormDialog";
import CountdownBanner from "@/components/CountdownBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  Plus,
  Upload,
  Download,
  LayoutGrid,
  Columns,
  BarChart3,
  X,
  CheckSquare,
  Square,
  TrendingUp,
  Map as MapIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { uyeleriExcelIndir } from "@/lib/excel";
import confetti from "canvas-confetti";
import { useAuth } from "@/context/AuthContext";
import {
  STATUSES,
  STATUS_META,
  ENTITY_TYPES,
  ENTITY_META,
  DEFAULT_ELECTION_AT,
} from "@/lib/constants";
import MomentumDialog from "@/components/MomentumDialog";
import MemberDetailDialog from "@/components/MemberDetailDialog";
import QuickSearch from "@/components/QuickSearch";
const PAGE_SIZE = 40;
// Türkçe karakter ve büyük/küçük harf farkını yok sayar
const normalize = (v) =>
  String(v ?? "")
    .replace(/[İIı]/g, "i")
    .replace(/[Şş]/g, "s")
    .replace(/[Ğğ]/g, "g")
    .replace(/[Üü]/g, "u")
    .replace(/[Öö]/g, "o")
    .replace(/[Çç]/g, "c")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
export default function Dashboard() {
  const { canWrite, isViewer } = useAuth();
  const [members, setMembers] = useState([]);
  // Durum listesi tek yerden (constants) gelsin: yeni bir durum eklenince
  // burası kendiliğinden uysun.
  const bosSayac = () => Object.fromEntries(STATUSES.map((s) => [s, 0]));
  const [stats, setStats] = useState({ total: 0, ...bosSayac() });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [tab, setTab] = useState("kanban");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileInputRef = useRef(null);
  const urlParams = new URLSearchParams(window.location.search);
  const showImport = urlParams.has("import");
  // Yeni uye ekleme gunluk kullanimda gorunmuyor; ?ekle ile aciliyor.
  const showAdd = urlParams.has("ekle");
  const [momentumOpen, setMomentumOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  // Bulk selection
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("Askı");
  const [bulkField, setBulkField] = useState("status"); // "status" | "entity_type"
  const [bulkEntityType, setBulkEntityType] = useState("Şahıs");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const [electionAt, setElectionAt] = useState(DEFAULT_ELECTION_AT);
  const [exportOpen, setExportOpen] = useState(false);

  const loadAll = async () => {
    try {
      const [ms, st] = await Promise.all([api.listMembers(), api.stats()]);
      setMembers(ms);
      setStats(st);
    } catch {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
    // Seçim tarihi ayrı gelsin: gelmezse sayaç varsayılanla çizilmeye devam etsin.
    try {
      const s = await api.settings();
      if (s?.election_at) setElectionAt(s.election_at);
    } catch {
      /* ayarlar okunamazsa varsayılan tarih kullanılır */
    }
  };

  const saveElectionAt = async (iso) => {
    try {
      const s = await api.updateSettings({ election_at: iso });
      setElectionAt(s.election_at);
      toast.success("Seçim tarihi güncellendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tarih kaydedilemedi");
      // Sayaçtaki düzenleme kutusu açık kalsın: kapanırsa hata başarıyla karışıyor
      throw err;
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Kendiliğinden tazeleme: ana ekrana eklenmiş uygulama arka plandan dönünce
  // hemen, açık dururken dakikada bir üye listesi sessizce yeniden çekilir.
  // Form, toplu seçim ya da bir pencere açıkken ve yeni bir değişiklikten
  // hemen sonra atlanır. /stats çağrılmaz (her çağrı anlık görüntü yazıyor);
  // sayılar listeden hesaplanır.
  const sonYazimRef = useRef(0);
  const mesgulRef = useRef(false);
  mesgulRef.current =
    formOpen || selectionMode || !!detail || !!confirmDelete || exportOpen || momentumOpen || bulkConfirmOpen;
  const tazeleRef = useRef(null);
  tazeleRef.current = async () => {
    if (document.visibilityState !== "visible" || mesgulRef.current) return;
    if (Date.now() - sonYazimRef.current < 8000) return;
    try {
      const ms = await api.listMembers();
      if (mesgulRef.current || Date.now() - sonYazimRef.current < 8000) return;
      setMembers(ms);
      recomputeStatsLocal(ms);
    } catch {
      /* ağ yoksa bir sonraki denemede */
    }
  };
  useEffect(() => {
    let gizlendi = 0;
    const gorunurluk = () => {
      if (document.visibilityState === "hidden") gizlendi = Date.now();
      else if (gizlendi && Date.now() - gizlendi > 10000) tazeleRef.current?.();
    };
    const zamanlayici = setInterval(() => tazeleRef.current?.(), 60 * 1000);
    document.addEventListener("visibilitychange", gorunurluk);
    return () => {
      clearInterval(zamanlayici);
      document.removeEventListener("visibilitychange", gorunurluk);
    };
  }, []);

  const recomputeStatsLocal = (list) => {
    const s = { total: list.length, ...bosSayac() };
    const by_type = {};
    for (const st of STATUSES) by_type[st] = { Firma: 0, "Şahıs": 0 };
    for (const m of list) {
      if (s[m.status] !== undefined) s[m.status] += 1;
      const t = m.entity_type === "Şahıs" ? "Şahıs" : "Firma";
      if (by_type[m.status]) by_type[m.status][t] += 1;
    }
    setStats((prev) => ({ ...prev, ...s, by_type }));
  };

  const searchActive = search.trim().length > 0;

  // Arama sonuçlarını yalnızca Liste görünümünde göster (Kanban'da değil).
  useEffect(() => {
    if (searchActive && tab === "kanban") setTab("list");
  }, [searchActive, tab]);

const filteredList = useMemo(() => {
    const raw = search.trim();
    if (!raw) return members;
    const q = normalize(raw);
    if (!q) return members;

     // 1-3 haneli sayı (# ile veya #'siz) -> sıra numarası
    const orderMatch = raw.match(/^#?\s*(\d{1,3})$/);
    if (orderMatch) {
      const n = parseInt(orderMatch[1], 10);
      const exact = members.filter((m) => m.order_no === n);
      if (exact.length > 0) return exact;
    }

    const digits = raw.replace(/\D/g, "");
    const terms = q.split(" ").filter(Boolean);

    return members.filter((m) => {
      const hay = normalize(
        [
          m.name,
          m.company,
          m.committee,
          m.status,
          m.address,
          m.notes,
          m.phone,
          m.member_no,
          m.trade_reg_no,
          m.order_no != null ? "#" + m.order_no : "",
        ]
          .filter(Boolean)
          .join(" ")
      );
      if (terms.every((t) => hay.includes(t))) return true;
      if (digits.length >= 2) {
        const phoneDigits = (m.phone || "").replace(/\D/g, "");
        if (phoneDigits.includes(digits)) return true;
        if ((m.member_no || "").includes(digits)) return true;
        if ((m.trade_reg_no || "").includes(digits)) return true;
        if (m.order_no != null && String(m.order_no) === digits) return true;
      }
      return false;
    });
  }, [members, search]);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, tab]);

  const visibleList = useMemo(
    () => filteredList.slice(0, visibleCount),
    [filteredList, visibleCount]
  );

  const handleDragEnd = async (result) => {
    if (!canWrite) return;
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    const newStatus = destination.droppableId;
    if (!STATUSES.includes(newStatus)) return;

    sonYazimRef.current = Date.now();
    const prev = members;
    const next = members.map((m) =>
      m.id === draggableId ? { ...m, status: newStatus } : m
    );
    setMembers(next);
    recomputeStatsLocal(next);

    try {
      await api.updateMember(draggableId, { status: newStatus });
      toast.success(`Durum güncellendi: ${newStatus}`);
      // Yetki belgesi kolonuna yeni giren üye için küçük bir kutlama
      if (newStatus === "Yetki belgesi" && source.droppableId !== "Yetki belgesi") {
        confetti({
          particleCount: 70,
          spread: 65,
          startVelocity: 32,
          origin: { y: 0.7 },
          colors: ["#10B981", "#34D399", "#A7F3D0", "#FFFFFF"],
          disableForReducedMotion: true,
        });
      }
    } catch {
      setMembers(prev);
      recomputeStatsLocal(prev);
      toast.error("Güncelleme başarısız");
    }
  };

  const handleSaved = (member, kind) => {
    sonYazimRef.current = Date.now();
    let next;
    if (kind === "create") {
      next = [member, ...members];
    } else {
      next = members.map((m) => (m.id === member.id ? member : m));
    }
    setMembers(next);
    recomputeStatsLocal(next);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    try {
      await api.deleteMember(id);
      const next = members.filter((m) => m.id !== id);
      setMembers(next);
      recomputeStatsLocal(next);
      toast.success("Üye silindi");
    } catch {
      toast.error("Silme başarısız");
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleExport = () => {
    try {
      const adet = uyeleriExcelIndir(members);
      toast.success(`${adet} üye Excel dosyasına aktarıldı`);
    } catch {
      toast.error("Excel dosyası oluşturulamadı");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { created } = await api.importCsv(file);
      toast.success(`${created} üye içe aktarıldı`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İçe aktarma başarısız");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Bulk selection helpers
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const exitSelectionMode = () => {
    setSelectionMode(false);
    clearSelection();
  };
  const enterSelectionMode = () => {
    setSelectionMode(true);
    setTab("list");
  };

  const applyBulkStatus = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const alan = bulkField === "entity_type" ? "entity_type" : "status";
    const deger = alan === "entity_type" ? bulkEntityType : bulkStatus;
    sonYazimRef.current = Date.now();
    setBulkBusy(true);
    try {
      const { updated } =
        alan === "entity_type"
          ? await api.bulkUpdateEntityType(ids, deger)
          : await api.bulkUpdateStatus(ids, deger);
      const idSet = new Set(ids);
      const next = members.map((m) => (idSet.has(m.id) ? { ...m, [alan]: deger } : m));
      setMembers(next);
      recomputeStatsLocal(next);
      toast.success(`${updated} üye "${deger}" olarak güncellendi`);
      exitSelectionMode();
    } catch {
      toast.error("Toplu güncelleme başarısız");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] dark:bg-neutral-950">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-5 sm:py-6 space-y-4 sm:space-y-5">
        {/* Seçim sayacı */}
        <CountdownBanner
          targetIso={electionAt}
          canWrite={canWrite}
          onSave={saveElectionAt}
        />

        {/* Title row */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500 dark:text-neutral-400">
              İnegöl Ticaret ve Sanayi Odası
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-1 text-neutral-950 dark:text-neutral-50">
              Seçmen Yönetim Paneli
            </h1>
          </div>
          {/* Mobilde iki sıraya yayılan ızgara: buton yazıları her ekranda görünür.
              Önceden yazılar `hidden sm:inline` ile gizleniyordu. */}
          <div className="grid grid-cols-2 sm:flex sm:items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImport}
              className="hidden"
              data-testid="import-file-input"
            />
            {canWrite && showImport && (
              <Button
                variant="outline"
                size="sm"
                className="h-10"
                onClick={() => fileInputRef.current?.click()}
                data-testid="import-btn"
              >
                <Upload className="h-4 w-4 mr-1.5" />
                <span>CSV Yükle</span>
              </Button>
            )}
            <Link to="/rota" className="w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-10 w-full sm:w-auto justify-center"
                data-testid="rota-btn"
              >
                <MapIcon className="h-4 w-4 mr-1.5" />
                <span>Rota</span>
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              className="h-10 w-full sm:w-auto justify-center"
              onClick={() => setMomentumOpen(true)}
              data-testid="momentum-btn"
            >
              <TrendingUp className="h-4 w-4 mr-1.5" />
              <span>Değişim</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-10 w-full sm:w-auto justify-center"
              onClick={() => setExportOpen(true)}
              data-testid="export-btn"
            >
              <Download className="h-4 w-4 mr-1.5" />
              <span>Excel'e Aktar</span>
            </Button>
            {canWrite && (
              <Button
                variant={selectionMode ? "default" : "outline"}
                size="sm"
                className={`h-10 w-full sm:w-auto justify-center ${selectionMode ? "bg-neutral-950 text-white hover:bg-neutral-800" : ""}`}
                onClick={selectionMode ? exitSelectionMode : enterSelectionMode}
                data-testid="selection-mode-btn"
              >
                <CheckSquare className="h-4 w-4 mr-1.5" />
                <span>{selectionMode ? "Seçimi Bitir" : "Toplu Seç"}</span>
              </Button>
            )}
            {canWrite && showAdd && (
              <Button
                size="sm"
                className="h-10 w-full sm:w-auto justify-center bg-neutral-950 hover:bg-neutral-800 text-white"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                data-testid="add-member-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Yeni Üye
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <StatsPanel stats={stats} />

        {/* Search */}
        <div className="flex flex-col gap-1.5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sıra no, isim, şirket, sicil no veya telefon ara..."
              className="pl-10 h-11 bg-white dark:bg-neutral-900 dark:border-neutral-800"
              data-testid="search-input"
            />
            {!search && (
              <kbd className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-0.5 rounded border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 px-1.5 py-0.5 text-[10px] font-mono text-neutral-400 pointer-events-none">
                Ctrl K
              </kbd>
            )}
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded hover:bg-neutral-100 dark:hover:bg-neutral-800"
                data-testid="search-clear"
              >
                <X className="h-3.5 w-3.5 text-neutral-500" />
              </button>
            )}
          </div>
          {searchActive && (
            <div className="text-[11px] text-neutral-500 dark:text-neutral-400 pl-1" data-testid="search-result-hint">
              <span className="font-mono font-semibold text-neutral-800 dark:text-neutral-200">{filteredList.length}</span> sonuç · Liste görünümünde gösteriliyor
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="grid grid-cols-2 w-full sm:w-auto sm:inline-grid bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-1 h-11"
            data-testid="view-tabs"
          >
            <TabsTrigger
              value="kanban"
              className="h-9 data-[state=active]:bg-neutral-950 data-[state=active]:text-white dark:data-[state=active]:bg-neutral-100 dark:data-[state=active]:text-neutral-950 gap-1.5"
              data-testid="tab-kanban"
            >
              <Columns className="h-4 w-4" />
              Kanban
            </TabsTrigger>
            <TabsTrigger
              value="list"
              className="h-9 data-[state=active]:bg-neutral-950 data-[state=active]:text-white dark:data-[state=active]:bg-neutral-100 dark:data-[state=active]:text-neutral-950 gap-1.5"
              data-testid="tab-list"
            >
              <LayoutGrid className="h-4 w-4" />
              Liste
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kanban" className="mt-4">
            {loading ? (
              <div className="text-center py-16 text-neutral-500 dark:text-neutral-400 text-sm">Yükleniyor...</div>
            ) : (
              <KanbanBoard
                members={members}
                onDragEnd={handleDragEnd}
                onEdit={(m) => {
                  setEditing(m);
                  setFormOpen(true);
                }}
                onDelete={(m) => setConfirmDelete(m)}
                onOpenDetail={(m) => setDetail(m)}
                canWrite={canWrite}
              />
            )}
          </TabsContent>

          <TabsContent value="list" className="mt-4">
            {loading ? (
              <div className="text-center py-16 text-neutral-500 dark:text-neutral-400 text-sm">Yükleniyor...</div>
            ) : (
              <>
                <MembersList
                  members={visibleList}
                  onEdit={(m) => {
                    setEditing(m);
                    setFormOpen(true);
                  }}
                  onDelete={(m) => setConfirmDelete(m)}
                  onOpenDetail={(m) => setDetail(m)}
                  selectionMode={selectionMode}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  canWrite={canWrite}
                />
                {filteredList.length > visibleCount && (
                  <div className="flex flex-col items-center gap-2 py-6">
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                      {visibleList.length} / {filteredList.length} üye gösteriliyor
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      data-testid="load-more"
                    >
                      Daha fazla göster
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

        </Tabs>
      </main>

      <QuickSearch
        members={members}
        onOpenDetail={(m) => setDetail(m)}
        onExport={() => setExportOpen(true)}
      />

      <MomentumDialog open={momentumOpen} onOpenChange={setMomentumOpen} />

      <MemberDetailDialog
        member={detail}
        onOpenChange={(open) => !open && setDetail(null)}
        canWrite={canWrite}
        onEdit={(m) => {
          setDetail(null);
          setEditing(m);
          setFormOpen(true);
        }}
      />

      <MemberFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        member={editing}
        onSaved={handleSaved}
      />

      {/* Mobil yenile dugmesi — toplu secim cubugu aciksa gizlenir */}
      <RefreshButton hidden={selectionMode} />

      {/* Bulk action floating bar */}
      {selectionMode && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-2rem)] max-w-2xl bg-neutral-950 text-white rounded-xl shadow-2xl border border-neutral-800 px-3 py-2.5 flex flex-wrap sm:flex-nowrap items-center gap-2"
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span
              className="inline-flex items-center justify-center h-8 min-w-[2.25rem] px-2 rounded-md bg-white/10 text-sm font-mono font-semibold"
              data-testid="bulk-selected-count"
            >
              {selectedIds.size}
            </span>
            <span className="text-xs text-neutral-300 hidden sm:inline">
              üye seçildi
            </span>
            {/* "Tümünü seç" kaldırıldı (29 Eylül 2026): tek tıkla tüm üyeleri
                toplu değiştirmek 28 Eylül'de 417 üyeyi yanlış duruma çekti.
                Toplu işlem artık yalnız tek tek seçilen üyelerle yapılır. */}
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={clearSelection}
                className="h-8 text-xs text-neutral-400 hover:text-white hover:bg-white/10"
                data-testid="bulk-clear"
              >
                Temizle
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Hangi alan toplu değişecek: durum mu, şirket türü mü */}
            <Select value={bulkField} onValueChange={setBulkField}>
              <SelectTrigger
                className="h-9 min-w-[110px] bg-white/10 border-white/20 text-white hover:bg-white/15"
                data-testid="bulk-field-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="status" data-testid="bulk-field-status">Durum</SelectItem>
                <SelectItem value="entity_type" data-testid="bulk-field-entity">Şirket Türü</SelectItem>
              </SelectContent>
            </Select>

            {bulkField === "entity_type" ? (
              <Select value={bulkEntityType} onValueChange={setBulkEntityType}>
                <SelectTrigger
                  className="h-9 min-w-[140px] bg-white/10 border-white/20 text-white hover:bg-white/15"
                  data-testid="bulk-entity-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((t) => {
                    const em = ENTITY_META[t];
                    return (
                      <SelectItem key={t} value={t} data-testid={`bulk-entity-${em.key}`}>
                        <div className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ background: em.dot }}
                          />
                          {em.long} — {em.vote}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger
                  className="h-9 min-w-[140px] bg-white/10 border-white/20 text-white hover:bg-white/15"
                  data-testid="bulk-status-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => {
                    const meta = STATUS_META[s];
                    return (
                      <SelectItem key={s} value={s} data-testid={`bulk-status-${meta.key}`}>
                        <div className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ background: meta.dot }}
                          />
                          {meta.option}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            <Button
              size="sm"
              disabled={selectedIds.size === 0 || bulkBusy}
              onClick={() => setBulkConfirmOpen(true)}
              className="h-9 bg-white text-neutral-950 hover:bg-neutral-100 font-medium"
              data-testid="bulk-apply-btn"
            >
              {bulkBusy ? "..." : "Uygula"}
            </Button>
          </div>
        </div>
      )}

      {/* Toplu işlem onayı: 28 Eylül kazasından sonra hiçbir toplu değişiklik
          sorulmadan uygulanmıyor. */}
      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Seçili <strong>{selectedIds.size} üyenin</strong>{" "}
                  {bulkField === "entity_type" ? "şirket türü" : "durumu"}{" "}
                  <strong>
                    "
                    {bulkField === "entity_type"
                      ? ENTITY_META[bulkEntityType]?.long
                      : STATUS_META[bulkStatus]?.option}
                    "
                  </strong>{" "}
                  olarak değişecek.
                </p>
                <ul className="text-[12px] text-neutral-600 dark:text-neutral-300 list-disc pl-5">
                  {members
                    .filter((m) => selectedIds.has(m.id))
                    .slice(0, 5)
                    .map((m) => (
                      <li key={m.id}>{m.name}</li>
                    ))}
                  {selectedIds.size > 5 && <li>ve {selectedIds.size - 5} üye daha</li>}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-confirm-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={applyBulkStatus}
              className="bg-neutral-950 hover:bg-neutral-800"
              data-testid="bulk-confirm-apply"
            >
              Evet, uygula
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Excel'e aktarma onayı */}
      <AlertDialog open={exportOpen} onOpenChange={setExportOpen}>
        <AlertDialogContent data-testid="export-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Excel tablosu olarak indirilsin mi?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Listedeki <strong>{stats.total}</strong> üyenin tamamı — ad, firma,
                  sicil numaraları, telefon, adres, durum, şirket türü ve notlar —
                  tek bir tablo dosyasına aktarılacak.
                </p>
                <p className="text-[12px] text-neutral-500 dark:text-neutral-400">
                  Dosya <span className="font-mono">uyeler-{new Date().toISOString().slice(0, 10)}.xlsx</span>{" "}
                  adıyla iner; sütun genişlikleri ayarlı ve başlık satırı süzülebilir.
                  Telefon numaraları ve notlar da içinde olduğu için dosyayı paylaşırken dikkatli olun.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="export-cancel-btn">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExport}
              className="bg-neutral-950 hover:bg-neutral-800"
              data-testid="export-confirm-btn"
            >
              İndir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Üyeyi Sil?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{confirmDelete?.name}</strong> adlı üye kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
